/*
 * Created by Ali Simsek on 5/14/20, 2:02 PM
 * Copyright (c) 2020 Carla Car Rental, Inc. All rights reserved.
 * Last modified 5/13/20, 4:30 PM
 */

import React from 'react'
import styled, { css } from 'styled-components'
import { themeGet } from 'styled-system'
import { FadeModal } from '../Modal'
import Loading from '../Loading'
import { CheckboxDown, Location, Plane } from '../svg'
import { ButtonEl, El, FlexEl, InputEl } from '../layout'
import { IoIosArrowBack } from 'react-icons/io'

// composed of label, input and menu
export const SelectContainerDS = styled(El)`
  position: relative;
  width: 100%;
  min-height: 50px;
`

export const LabelDS = styled.label`
  display: block;
  width: 100%;
  line-height: 14px;
  padding-bottom: 2px;
  font-weight: normal;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${themeGet('colors.textGrey')};
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    height: 20px;
    padding-bottom: 6px;
    font-size: 14px;
    font-weight: 500;
    color: ${themeGet('colors.textGrey')};
  }
`

const inputDSStyle = css`
  background-color: white;
  color: ${themeGet('colors.textBlack')};
  border-radius: 4px;
  border: solid 1px #dfdfe7;
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    border: solid 2px #dfdfe7;
    border-radius: 12px;
  }
  &::placeholder {
    transition: all 100ms ease;
    color: #b4b4c2;
  }
  &:focus,
  &.focus,
  .focus > & {
    object-fit: contain;
    border: solid 2px ${themeGet('colors.colorPrimary')};
    color: ${themeGet('colors.textBlack')};
    &::placeholder {
      color: #b4b4c2;
    }
  }
`

export const InputDS = styled(InputEl)`
  width: 100%;
  text-align: left;
  font-weight: 500;
  white-space: nowrap;
  ${inputDSStyle}
`

// @TODO change with InputEl?
export const SelectDS = styled(ButtonEl)`
  min-height: 36px;
  padding: 10px;
  width: 100%;
  position: relative;
  border-radius: 3px;
  border: 1px solid ${themeGet('colors.blueGrey.2')};
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  transition: all 100ms ease;
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    min-height: 50px;
    white-space: nowrap;
  }
  ${inputDSStyle}
`

export const MenuDS = styled(El)`
  ${(props) => (!props.isOpen ? `display:none` : '')};
  margin-top: 10px;
  border-radius: 3px;
  font-size: 16px;
  background-color: white;
  max-height: 400px;
  overflow: scroll;
  z-index: 23;
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    white-space: nowrap;
    border-radius: 12px;
    box-shadow: 0 0 6px 2px rgba(111, 112, 115, 0.24);
    &.items,
    & .items {
      .item {
        height: 50px;
        padding: 10px;
        line-height: 30px;
        align-items: center;
      }
    }
  }
  li {
    list-style: none;
    &[aria-selected='true'] {
      background-color: ${themeGet('colors.newColorPrimary')};
      cursor: pointer;
    }
  }
`

// container for mobile downshift menu (and input)
export const ModalContainerDS = styled(El)`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

// element containing loading icon or error text on both mobile/desktop
export const MenuPlaceholderDS = styled(El)`
  text-align: center;
  margin: 20vh auto;
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    margin: 50px auto;
  }
`

// navbar like downshift input/button container in the menu
export const ModalHeaderDS = styled(({ closeMenu, label, value, children, ...props }) => (
  <FlexEl {...props}>
    <El className='back-icon'>
      <IoIosArrowBack size={24} onClick={closeMenu} />
    </El>
    <El className='select-container'>
      <LabelDS>{label}</LabelDS>
      {children}
    </El>
  </FlexEl>
))`
  position: fixed;
  /* top: 40px; */
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 10px;
  align-items: flex-end;
  .back-icon {
    font-size: 22px;
    padding: 3px 8px 3px 0;
  }
  .select-container {
    flex-grow: 1;
  }
`

// scrollable element covering rest of ModalHeaderDS
export const ModalItemsContainer = styled(El)`
  max-height: 100vh;
  padding-top: 100px;
  overflow-y: scroll;
`

export const ModalInputDS = styled(InputEl)`
  width: 100%;
  height: 40px;
  padding-right: 20px;
  padding-left: 20px;
  border-radius: 20px;
  background-color: ${themeGet('colors.newColorPrimary')};
  border: 0;
  font-weight: bold;
  color: #263238;
  &:hover,
  &:focus {
    box-shadow: none;
  }
`

export const ModalSelectDS = styled(ButtonEl)`
  width: 100%;
  text-align: left;
  height: 40px;
  padding-left: 20px;
  border-radius: 20px;
  background-color: ${themeGet('colors.newColorPrimary')};
  border: 0;
  font-size: 16px;
  font-weight: bold;
  color: #263238;
`

export const CheckboxDownDS = styled((props) => (
  <El {...props}>
    <CheckboxDown />
  </El>
))`
  position: absolute;
  right: 9px;
  bottom: 9px;
`

export const MobileMenuDS = styled(
  ({ isOpen, closeMenu, items, label, placeholder, selectedItem, getMenuProps, getItemProps, ...props }) => (
    <MenuDS isOpen={isOpen} {...getMenuProps()} {...props}>
      <FadeModal isOpen={isOpen} onClose={closeMenu}>
        <ModalContainerDS>
          <ModalHeaderDS label={label} closeMenu={closeMenu}>
            <ModalSelectDS type='button' onClick={closeMenu} width={1}>
              {selectedItem ? selectedItem.label : placeholder}
            </ModalSelectDS>
          </ModalHeaderDS>
          <ModalItemsContainer>
            <ul className='items'>
              {items.map((item, index) => (
                <li className='item' {...getItemProps({ key: item.value, item, index })}>
                  {item.label}
                </li>
              ))}
            </ul>
          </ModalItemsContainer>
        </ModalContainerDS>
      </FadeModal>
    </MenuDS>
  )
)`
  .select-container {
    flex-grow: 1;
  }
  ul.items {
    li.item {
      text-align: center;
      height: 60px;
      line-height: 60px;
    }
  }
`

export const AgeMenuDS = styled(El)`
  ${(props) => (!props.isOpen ? `display:none` : '')};
  margin-top: 10px;
  border-radius: 3px;
  font-size: 16px;
  background-color: white;
  max-height: 400px;
  overflow: scroll;
  @media (min-width: ${themeGet('breakpoints.0')}px) {
    white-space: nowrap;
    border-radius: 12px;
    box-shadow: 0 0 6px 2px rgba(111, 112, 115, 0.24);
    &.items,
    & .items {
      .item {
        height: 50px;
        padding: 10px;
        line-height: 30px;
        align-items: center;
      }
    }
  }
  li {
    list-style: none;
    &[aria-selected='true'] {
      background-color: ${themeGet('colors.newColorPrimary')};
      cursor: pointer;
    }
  }
`

const AgeModalHeaderDS = styled(ModalHeaderDS)`
  margin-top: 10px;
  padding-top: 6px;
  color: ${themeGet('colors.textGrey')} !important;
`

export const AgeMobileDS = styled(
  ({ isOpen, closeMenu, items, label, placeholder, selectedItem, getMenuProps, getItemProps, ...props }) => (
    <AgeMenuDS isOpen={isOpen} {...getMenuProps()} {...props}>
      <FadeModal isOpen={isOpen} onClose={closeMenu}>
        <ModalContainerDS>
          <AgeModalHeaderDS label={label} closeMenu={closeMenu}>
            <ModalSelectDS type='button' onClick={closeMenu} width={1}>
              {selectedItem ? selectedItem.label : placeholder}
            </ModalSelectDS>
          </AgeModalHeaderDS>
          <ModalItemsContainer>
            <ul className='items'>
              {items.map((item, index) => (
                <li
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  className='item'
                  {...getItemProps({ key: item.value, item, index })}
                >
                  <div>{item.label}</div>
                </li>
              ))}
            </ul>
          </ModalItemsContainer>
        </ModalContainerDS>
      </FadeModal>
    </AgeMenuDS>
  )
)`
  .select-container {
    flex-grow: 1;
  }
  ul.items {
    li.item {
      text-align: center;
      height: 60px;
      line-height: 60px;
    }
  }
`

// clear the content of neighborhood input element of clicked element
// const clearInput = e => {
//   e.target.closest('div').querySelector('input').value = ''
// }

export const AsyncMobileMenu = styled(
  ({
    isOpen,
    isLoading,
    label,
    placeholder,
    error,
    errorText,
    items,
    closeMenu,
    getMenuProps,
    getItemProps,
    getInputProps,
    ...props
  }) => (
    <MenuDS {...getMenuProps()} {...props} isOpen={isOpen}>
      <FadeModal isOpen={isOpen} onClose={closeMenu}>
        <ModalContainerDS>
          <ModalHeaderDS label={label} closeMenu={closeMenu}>
            <ModalInputDS placeholder={placeholder} {...getInputProps(/* {onFocus: @TODO clear input)*/)} autoFocus />
          </ModalHeaderDS>
          <ModalItemsContainer>
            {isLoading && (
              <MenuPlaceholderDS>
                <Loading />
              </MenuPlaceholderDS>
            )}
            {error && <MenuPlaceholderDS>{errorText}</MenuPlaceholderDS>}
            {items && items.length > 0 && (
              <ul className='items'>
                {items.map((item, index) => (
                  <FlexEl as='li' className='item' {...getItemProps({ key: item.value, item, index })}>
                    <El>{item.label}</El>
                    <El>{item.type === 'airport' ? <Plane /> : <Location />}</El>
                  </FlexEl>
                ))}
              </ul>
            )}
            {!isLoading && !error && !(items && items.length > 0) && (
              <MenuPlaceholderDS>Enter city or airport</MenuPlaceholderDS>
            )}
          </ModalItemsContainer>
        </ModalContainerDS>
      </FadeModal>
    </MenuDS>
  )
)`
  .items {
    .item {
      display:flex;
      flex-wrap: nowrap;
      justify-content:space-between;
      align-items:center;
      height: 60px;
      padding: 10px 15px;
      .location { fill: ${themeGet('colors.blueGrey.6')};
      .plane { fill: ${themeGet('colors.blueGrey.6')}; }
  }
`
